<template>
	<div>
		<Restore @success="close('Restore')" v-slot="{ restore }">
			<Page animate :theme="pageTheme" :custom-styles="pageStyles">
				<template v-slot:content>
					<SafeAreaTop
						:class="{ absolute: !imageBlockDisabled }"
						@close="onClose"
						:closePosition="closePosition"
						:showClose="!isCloseButtonHidden"
						:theme="closeTheme"
					/>
					<TopVideo :with-video="videoIsVisible" :custom-style="videoStyles" v-if="!imageBlockDisabled" />

					<div class="container" :class="{ 'image-block-disabled': imageBlockDisabled }">
						<component :is="titleComponentDef.component" :user-data="user" :title-styles="titleComponentDef.styles">
							<div v-if="titleComponentDef.textIsHTML" v-html="titleComponentDef.text"></div>
							<template v-else>
								{{ titleComponentDef.text ? t(titleComponentDef.text) : t('choose_your_plan') }}
							</template>
						</component>

						<Subtitle v-if="subtitle">
							<span v-html="subtitle"></span>
						</Subtitle>

						<CommentsSlider v-if="reviewsIsVisibe" :showPaginate="!isAndroid" />

						<ProductsContainer
							:is-universal-paywall="isUniversalPaywall"
							:is-android="isAndroid"
							@change="handleChangeProduct"
						/>

						<template v-if="linksPosition == 'after_products'">
							<ProductTerms v-if="activeProduct && activeProduct.terms" :terms="activeProduct.terms" />
							<PaywallLinksBlock v-if="!isAndroid" @restore="restore" />
						</template>

						<ListMain v-if="listIsVisibe" :items="listItems" />
					</div>

					<Prompt
						:isOpen="showPaywallCancelPrompt"
						:title="t('cancel_popup.title')"
						:text="t('cancel_popup.body', { store: !isAndroid ? 'iTunes Store' : 'Play Store' })"
						:action="t('cancel_popup.action')"
						@onClose="onClosePaywallCancelPrompt"
						@onAction="onActionPaywallCancelPrompt"
					/>

					<Popup
						v-if="isShowingWebPaymentPopup"
						@close="closeWebPaymentPopup"
						title="PaymentMethodPopup"
						:path="['OnboardingPaywall']"
						closable
					>
						<BillingForm @purchase="onPurchase" :product="activeProduct" />
					</Popup>
					<Popup v-if="isShowingSuccessPopup" title="SuccessfulPaymentPopup" :path="['OnboardingPaywall']">
						<SuccessMessage @close="onCloseSuccessPopup" />
					</Popup>
				</template>

				<template v-slot:footer>
					<div class="container">
						<PaywallLinksBlock class="mb-lg" v-if="linksPosition == 'before_button'" @restore="restore" />

						<p-button
							@click.prevent="handleSubmitButtonClick"
							title="ContinueButton"
							:path="['OnboardingPaywall']"
							label=""
							:loading="isLoading"
							cls="btn-primary btn-block"
						>
							{{ continueButtonText }}
						</p-button>

						<ProductTerms v-if="canMakeWebPayments" :terms="t('billing.available_payment_methods')" />
					</div>
				</template>

				<template v-slot:links>
					<template v-if="linksPosition == 'after_button'">
						<div class="container">
							<ProductTerms v-if="activeProduct && activeProduct.terms" :terms="activeProduct.terms" />
							<PaywallLinksBlock @restore="restore" />
						</div>
					</template>
				</template>
			</Page>
		</Restore>
	</div>
</template>

<script>
import Page from '@/components/Page'
import Restore from '@/components/Restore'
import TopVideo from '@/components/Video'
import CommentsSlider from '@/components/CommentsSlider'
import Prompt from '@/components/Prompt'
import PButton from '@/components/Button'
import Popup from '@/components/Popup'
import PaywallLinksBlock from '@/components/PaywallLinksBlock'
import { computed, ref } from '@vue/reactivity'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import useApi from '@/composition/api.js'
import useConfig from '@/composition/config.js'
import SafeAreaTop from '@/components/SafeAreaTop.vue'
import { inject } from '@vue/runtime-core'
import ProductsContainer from '@/components/products/ProductsContainer.vue'
import ProductTerms from '@/components/products/ProductTerms'
import Title from '@/components/title/Title.vue'
import TitleWithWeightTarget from '@/components/title/TitleWithWeightTarget.vue'
import ListMain from '@/components/list/ListMain.vue'
import { BillingForm, SuccessMessage, useBilling } from '@/modules/billing'
import Subtitle from '@/components/Subtitle.vue'

export default {
	components: {
		Page,
		Restore,
		TopVideo,
		CommentsSlider,
		SafeAreaTop,
		PButton,
		Prompt,
		PaywallLinksBlock,
		ProductsContainer,
		ProductTerms,
		Title,
		TitleWithWeightTarget,
		ListMain,
		Popup,
		BillingForm,
		SuccessMessage,
		Subtitle
	},

	setup() {
		try {
			const { t } = useI18n()
			const amplitude = inject('amplitude')
			const store = useStore()
			const api = useApi()
			const config = useConfig()
			const router = useRouter()

			const isAndroid = api.getPlatform() == 'android'
			const upgradeModalComponent = ref(null)
			const showUpgradeModal = ref(false)
			const showPaywallCancelPrompt = ref(false)
			const activeProduct = ref(null)
			const lastPaymentMethod = computed(() => store.state.lastPaymentMethod)
			const isCloseButtonHidden = ref(false)

			// General
			const user = computed(() => store.state.user.data || {})
			const isSumbitHandlingRequired = computed(() =>
				config.get({ key: 'paywall.handle_submit', default_value: false })
			)
			const isNeedCloseAfterPaywall = computed(() =>
				config.get({ key: 'paywall.close_after_paywall', default_value: false })
			)
			const purchaseOnChangeProduct = computed(() =>
				config.get({ key: 'onChangeProductPurchase', default_value: false })
			)
			const afterSubscription = computed(() => config.get({ key: 'after_subscription', default_value: null }))
			const next = computed(() => config.get({ key: 'postPaid.type', default_value: 'workbooks' }))
			const offerTrial = computed(() => config.get({ key: 'paywall.offerTrialAfterClose', default_value: false }))
			const listItems = computed(() => config.get({ key: 'paywall.list.items', default_value: [] }))

			const isLoading = computed(() => store.state.isLoading)

			// Styling

			const closeTheme = computed(() => config.get({ key: 'paywall.close_button_theme', default_value: 'dark' }))
			const isUniversalPaywall = computed(() => config.get({ key: 'paywall.universal_paywall', default_value: false }))

			const linksPosition = computed(() =>
				config.get({ key: 'paywall.links_position', default_value: 'after_products' })
			)
			const closePosition = computed(() => config.get({ key: 'paywall.close_buttom.position', default_value: 'left' }))

			const pageTheme = computed(() => config.get({ key: 'paywall.page.theme', default_value: 'default' }))
			const pageStyles = computed(() => config.get({ key: 'paywall.page.style', default_value: {} }))
			const videoStyles = computed(() => config.get({ key: 'paywall.video_style', default_value: {} }))

			const videoIsVisible = computed(() => config.get({ key: 'paywall.with_video', default_value: true }))
			const reviewsIsVisibe = computed(() => !config.get({ key: 'paywall.hide_reviews', default_value: false }))
			const listIsVisibe = computed(() => Boolean(config.get({ key: 'paywall.list', default_value: null })))
			const continueButtonText = computed(() =>
				config.get({ key: 'paywall.continueButtonText', default_value: t('continue') })
			)
			const imageBlockDisabled = computed(() => config.get({ key: 'paywall.imageBlockDisabled', default_value: false }))

			// Content definitions

			const titleComponentDef = computed(() => {
				const component = config.get({ key: 'paywall.title.component', default_value: 'Title' })
				const text = config.get({ key: 'paywall.title.text_key', default_value: 'choose_your_plan' })
				const textIsHTML = config.get({ key: 'paywall.title.textIsHTML', default_value: false })
				const styles = config.get({ key: 'paywall.title.styles', default_value: {} })

				return {
					component,
					styles,
					text,
					textIsHTML
				}
			})

			const subtitle = computed(() => config.get({ key: 'paywall.subtitle', default_value: null }))

			const close = (Status = 'Close', nextRoute = null, product = {}) => {
				amplitude.logEvent('Paywall - Close', {
					Layout: 'Workbooks',
					From: store.getters['getSource'],
					Step: 'Paywall',
					Status
				})

				amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
					action: Status.toLowerCase(),
					...product
				})

				if (isNeedCloseAfterPaywall.value) {
					api.close()
				} else {
					router.push({
						name: nextRoute || next.value,
						query: {
							Status
						}
					})
				}
			}

			const onClose = () => close('Close', offerTrial.value ? 'offer-trial' : undefined)

			const hideCloseButtonTillBackPressed = computed(() =>
				config.get({ key: 'paywall.hideCloseButtonTillBackPressed', default_value: false })
			)

			if (hideCloseButtonTillBackPressed.value) {
				isCloseButtonHidden.value = true

				window.fs_backPressed = function () {
					if (isCloseButtonHidden.value) {
						isCloseButtonHidden.value = false

						return true
					}

					return false
				}
			}

			const purchase = (product) => {
				api.purchase({
					productId: product.id,
					onFinish: () => {
						const provider = isAndroid ? 'google_play' : 'apple_pay'
						store.commit('setLastPaymentMethod', provider)
						finishPurchase()
					},
					onError: () => {
						console.log('error')
					},
					onCancel: () => {
						showPaywallCancelPrompt.value = true
					},
					closeOnSuccess: false
				})
			}

			const finishPurchase = () => {
				store.commit('setPurchaseSubscription', true)

				amplitude.logEvent('Paywall - Done', {
					Layout: 'Workbooks',
					From: store.getters['getSource'],
					Step: 'Paywall',
					ProductId: activeProduct.value.id,
					Status: 'Subscribe'
				})

				close('Subscribe', afterSubscription.value || next.value, {
					product_id: activeProduct.value.id,
					duration_month: activeProduct.value.duration_months,
					price: Math.abs(activeProduct.value.product_price),
					currency: activeProduct.value.currency,
					provider_name: lastPaymentMethod.value
				})
			}

			const handleChangeProduct = (product) => {
				if (activeProduct.value && product.id == activeProduct.value?.id && isAndroid) {
					handleSubmit()
				} else {
					activeProduct.value = product
					if (purchaseOnChangeProduct.value) {
						handleSubmit()
					}
				}
			}

			const onClosePaywallCancelPrompt = () => {
				showPaywallCancelPrompt.value = false
			}

			const onActionPaywallCancelPrompt = () => {
				showPaywallCancelPrompt.value = false
				purchase(activeProduct.value)
			}

			const handleSubmitButtonClick = () => {
				if (isSumbitHandlingRequired) {
					amplitude.logEvent('ButtonClick', {
						title: 'ContinueButton',
						path: [`${store.getters['getSource']}PaywallScreen`]
					})
				}
				handleSubmit()
			}

			const handleSubmit = () => {
				/** START: Web payments */
				if (canMakeWebPayments.value) {
					isShowingWebPaymentPopup.value = true
					return
				}
				/** END: Web payments */

				purchase(activeProduct.value)
			}

			/** START: Web payments */
			const billing = useBilling()
			const canMakeWebPayments = computed(() => billing.canMakeWebPayments.value(activeProduct.value?.currency))

			const isShowingWebPaymentPopup = ref(false)
			const isShowingSuccessPopup = ref(false)

			const purchaseInApp = () => {
				purchase(activeProduct.value)
			}

			const purchaseWeb = (data) => {
				const { provider, ...payload } = data
				return billing
					.createSubscription(provider, {
						...payload,
						...activeProduct.value
					})
					.then((subscription) => {
						lastPaymentMethod.value = subscription.store
						showSuccessPopup()
					})
			}

			const closeWebPaymentPopup = () => {
				isShowingWebPaymentPopup.value = false
			}

			const showSuccessPopup = () => {
				closeWebPaymentPopup()
				isShowingSuccessPopup.value = true
			}

			const onCloseSuccessPopup = () => finishPurchase()

			const onPurchase = (data) => {
				if (data.provider == 'InApp') {
					purchase(activeProduct.value)
				} else {
					purchaseWeb(data)
				}
			}
			/** END: Web payments */

			api.onBackBressed()

			return {
				t,
				titleComponentDef,
				user,
				isLoading,
				videoIsVisible,
				videoStyles,
				isUniversalPaywall,
				handleChangeProduct,
				handleSubmitButtonClick,
				handleSubmit,
				showPaywallCancelPrompt,
				onClosePaywallCancelPrompt,
				onActionPaywallCancelPrompt,
				onClose,
				closeTheme,
				closePosition,
				upgradeModalComponent,
				showUpgradeModal,
				isAndroid,
				linksPosition,
				activeProduct,
				pageTheme,
				pageStyles,
				reviewsIsVisibe,
				listIsVisibe,
				listItems,
				close,
				continueButtonText,
				imageBlockDisabled,
				isShowingWebPaymentPopup,
				isShowingSuccessPopup,
				purchaseInApp,
				onCloseSuccessPopup,
				closeWebPaymentPopup,
				onPurchase,
				canMakeWebPayments,
				subtitle,
				isCloseButtonHidden
			}
		} catch (e) {
			document.getElementById('log').innerHTML = e
		}
	}
}
</script>

<style scoped lang="scss">
.absolute {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
	width: 100%;
}
.image-block-disabled {
	padding-top: 3rem;
}
</style>
